/**
 * Sentral event-type-registry for prosjekt-loggen (VIB-1184 C / D14).
 *
 * Erstatter de fire tidligere parallelle, manuelt vedlikeholdte listene
 * (EVENT_LABELS, EVENT_COLORS, FILTER_EVENT_TYPES, formatPayload) med én kilde.
 * Hver `project_events.event_type` SKAL ha en oppføring her: label + farge +
 * filter-kategori + en `format`-funksjon som leser de FAKTISKE payload-nøklene
 * DB-triggeren skriver. En ny DB-trigger uten registry-oppføring fanges av
 * event-registry.test.ts (CI-gate) — se EXPECTED_PROD_EVENT_TYPES der.
 */
import { formatNOK, formatHours } from "./format";
import { PROJECT_LOCATION_STATUS_LABEL } from "@/lib/locations/project-status";
import { formatSendTime } from "./send-window";
import { STAGE_LABELS } from "./opportunity-calc";
import { FOLLOW_UP_RULES, isFollowUpRuleKey } from "./follow-up-rules";
import { dismissReasonLabel } from "./signal-dismiss-reasons";
import type { OfferIdeaCardKind, OpportunityStage } from "./db-types";
import { IDEA_KIND_LABEL } from "./tilbudsarbeidsrom/idea-board";

export type EventFilter =
  | "all"
  | "tasks"
  | "invoices"
  | "vouchers"
  | "budget"
  | "meetings"
  | "comments"
  | "time"
  | "project"
  | "okonomi"
  | "dokumenter"
  | "utstyr"
  | "lokasjoner";

export interface EventTypeDef {
  label: string;
  color: string;
  filter: Exclude<EventFilter, "all">;
  /** Detalj-linje fra payload; null = ingen linje. */
  format?: (payload: Record<string, unknown>) => string | null;
}

export const FILTER_OPTIONS: { value: EventFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "tasks", label: "Oppgaver" },
  { value: "invoices", label: "Faktura" },
  { value: "vouchers", label: "Bilag" },
  { value: "budget", label: "Budsjett" },
  { value: "meetings", label: "Møter" },
  { value: "comments", label: "Kommentarer" },
  { value: "time", label: "Timer" },
  { value: "project", label: "Prosjekt" },
  { value: "okonomi", label: "Økonomi" },
  { value: "dokumenter", label: "Dokumenter" },
  { value: "utstyr", label: "Utstyr" },
  { value: "lokasjoner", label: "Lokasjoner" },
];

const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

const GREEN = "bg-green-100 text-green-800";
const RED = "bg-red-100 text-red-800";
const BLUE = "bg-blue-100 text-blue-800";
const PURPLE = "bg-purple-100 text-purple-800";
const AMBER = "bg-amber-100 text-amber-800";
const INDIGO = "bg-indigo-100 text-indigo-800";
const GRAY = "bg-gray-100 text-gray-700";

// Kontaktlogg-typer (CRM-1.3). Store forbokstaver: brukes som setningsstart ("Samtale med ...").
const LEAD_OUTCOME_NB: Record<string, string> = {
  no_answer: "Ikke svar",
  not_relevant: "Avvist",
  call_back: "Ring tilbake senere",
  meeting_booked: "Avtalt møte",
  email_sent: "Sendt e-post",
  wrong_number: "Feil nummer",
  email_planned: "Skal sende mail",
};
const REMINDER_KIND_NB: Record<string, string> = {
  day_before: "Dagen før",
  three_hours: "3 timer før",
  fifteen_minutes: "15 minutter før",
};
const EQUIPMENT_STATUS_NB: Record<string, string> = {
  ok: "Tilgjengelig",
  in_service: "På service",
  missing: "Savnet",
  sold: "Solgt",
  retired: "Utrangert",
};
const SERVICE_STATUS_NB: Record<string, string> = { open: "Åpen", at_vendor: "Hos verksted", resolved: "Ferdig" };
const NOTIFICATION_KIND_NB: Record<string, string> = {
  damage: "Skade", service_booking: "På service, booket", double_booking: "Dobbeltbooket",
};
const EQUIPMENT_FIELD_NB: Record<string, string> = {
  name: "navn", category_id: "kategori", department_id: "sted", quantity: "antall",
  serial_numbers: "serienummer", bookable: "kan bookes", insurance_value: "forsikringsverdi",
  purchase_price: "innkjøpspris", extra_price: "pris tilleggsutstyr", purchase_date: "innkjøpsdato",
  retired_date: "pensjonsdato", rental_price_day: "leiepris", parts: "deler og tilbehør", comment: "kommentar",
  product_id: "modell", product_skip_reason: "produktdata hoppes over",
};
const PROJECT_LOCATION_STATUS_NB: Record<string, string> = PROJECT_LOCATION_STATUS_LABEL;
const projectRef = (p: Record<string, unknown>): string | null =>
  [str(p.project_code), str(p.project_name)].filter(Boolean).join(" ") || null;

const LOCATION_FIELD_NB: Record<string, string> = {
  name: "navn", slug: "lenke", type_id: "type", address: "adresse", postal_code: "postnummer", postal_place: "poststed",
  municipality_number: "kommune", municipality_name: "kommune", district: "bydel", country_code: "land",
  lat: "posisjon", lon: "posisjon", position_source: "posisjon", google_place_id: "sted", tags: "stikkord",
  description: "beskrivelse", parking: "parkering", power: "strøm", light: "lys og sol", access: "tilgang",
  rental_price: "leiepris", contact_name: "kontakt", contact_phone: "kontakt", contact_email: "kontakt",
  links: "lenker", cover_image_id: "forsidebilde", archived_at: "arkivert",
};
const EQUIPMENT_PRODUCT_FIELD_NB: Record<string, string> = {
  brand_name: "merke", model: "modell", brand_id: "merke", category_id: "kategori", description: "beskrivelse",
  manufacturer_part_no: "artikkelnummer", specs: "spesifikasjoner", product_url: "produktside",
  enriched_at: "produktdata", enriched_by: "produktdata",
};
const MEDIA_KIND_NB: Record<string, string> = { image: "Bilde", manual: "Manual", datasheet: "Datablad", document: "Dokument" };
const formatIsoDateNb = (iso: string): string => iso.split("-").reverse().join(".");
const formatIsoRangeNb = (from: string, to: string): string =>
  from === to ? formatIsoDateNb(from) : `${formatIsoDateNb(from)}–${formatIsoDateNb(to)}`;
/** «631 Undergrunn · Red Komodo × 2 · 28.10.2026–30.10.2026 · Christer tar med» */
const bookingLineDetail = (p: Record<string, unknown>): string | null => {
  const qty = num(p.quantity);
  const item = str(p.item_name);
  const from = str(p.date_from);
  const to = str(p.date_to);
  return [
    str(p.label),
    item ? `${item}${qty && qty > 1 ? ` × ${qty}` : ""}` : null,
    from && to ? formatIsoRangeNb(from, to) : null,
    str(p.note),
  ].filter(Boolean).join(" · ") || null;
};
const formatDateTimeNb = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Oslo" });
};

const FOLLOW_UP_OUTCOME_NB: Record<string, string> = {
  email_sent: "Sendt e-post",
  phone_done: "Tok det på telefon",
  closed: "Avsluttet",
};

const ACTIVITY_TYPE_NB: Record<string, string> = {
  email_out: "E-post ut", email_in: "E-post inn", call: "Samtale", sms_out: "SMS ut",
  meeting: "Møte", note: "Notat", summary: "Referat",
};

const CUSTOMER_STATUS_NB: Record<string, string> = { warm: "Varm", medium: "Medium", cold: "Kald" };

const PROJECT_STATUS_NB: Record<string, string> = {
  active: "Aktiv", completed: "Fullført", cancelled: "Kansellert", kommende: "Kommende",
};
const PHASE_NB: Record<string, string> = {
  preprod: "Preproduksjon", production: "Produksjon", postprod: "Postproduksjon", faktura: "Faktura",
};
const TASK_STATUS_NB: Record<string, string> = {
  not_started: "Ikke startet", in_progress: "Pågår", completed: "Fullført",
  approved: "Godkjent", rejected: "Avvist",
};
const AGREEMENT_TYPE_NB: Record<string, string> = {
  recurring: "Løpende avtale", rental: "Utleieavtale", storage: "Lagringsavtale",
};
const AGREEMENT_INTERVAL_NB: Record<string, string> = {
  monthly: "månedlig", quarterly: "kvartalsvis", yearly: "årlig",
};

/** «Oppstart 60 % / Termin 20 % / Levering 20 %» fra invoice_terms-payload (VIB-2836). */
function formatTerms(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  return v
    .map((t) => {
      const term = (t ?? {}) as Record<string, unknown>;
      return `${str(term.label) ?? "—"} ${num(term.pct) ?? "—"} %`;
    })
    .join(" / ");
}

/** Felles formatter for oppgave-status-overganger (task_* events). */
/** «Branninstruks (dokument)» / «Protokoll.pdf (fil)» — VIB-2703. */
/**
 * VIB-2703: payloaden bærer med vilje ikke tittelen. `event_log` er lesbar for
 * alle innloggede, så en tittel der ville omgått mappas tilgangsnivå (se
 * begrunnelsen i migrasjonen). Linja sier derfor hva slags innhold det var;
 * hvilket dokument det gjelder står i `entity_id`, og tittelen leses i arkivet,
 * der tilgangen faktisk håndheves.
 */
function documentLine(p: Record<string, unknown>): string | null {
  const kind = str(p.kind);
  if (kind === "page") return "dokument";
  if (kind === "file") return "fil";
  return null;
}

/** «leses av Ansatt, skrives av Admin · Leder» — VIB-2703. Uten mappenavn. */
function folderAccessLine(p: Record<string, unknown>): string | null {
  const lese = ROLE_NB[str(p.read_min_role) ?? ""] ?? str(p.read_min_role);
  const skrive = ROLE_NB[str(p.write_min_role) ?? ""] ?? str(p.write_min_role);
  if (!lese || !skrive) return null;
  return `leses av ${lese}, skrives av ${skrive}`;
}

/** «lesetilgang» / «skrivetilgang» ut av can_read/can_write — VIB-2726. */
function grantLevel(read: unknown, write: unknown): string | null {
  if (write === true) return "skrivetilgang";
  if (read === true) return "lesetilgang";
  return null;
}

/**
 * VIB-2726: gruppe-events. Gruppenavnet ER med — `access_groups_select` sier at
 * navnet på en gruppe ikke er hemmelig, og uten det blir linja «én person ble
 * lagt til i en gruppe», som ingen kan bruke til noe. Mappenavn er derimot
 * fortsatt utelatt; der gjelder arkivets regel fra VIB-2703.
 */
function groupMemberLine(p: Record<string, unknown>): string | null {
  const group = str(p.group_name);
  return [group, str(p.user_id) ? "én person" : null].filter(Boolean).join(" · ") || null;
}

function groupDeletedLine(p: Record<string, unknown>): string | null {
  const name = str(p.name);
  const members = Array.isArray(p.member_ids) ? p.member_ids.length : null;
  const grants = Array.isArray(p.grants) ? p.grants.length : null;
  return [
    name,
    members != null
      ? `${members} ${members === 1 ? "person" : "personer"} mistet tilgang`
      : null,
    grants ? `${grants} ${grants === 1 ? "mappe" : "mapper"}` : null,
  ]
    .filter(Boolean)
    .join(" · ") || null;
}

function folderGrantLine(p: Record<string, unknown>): string | null {
  const group = str(p.group_name);
  const niva = grantLevel(p.can_read, p.can_write);
  return [group, niva].filter(Boolean).join(" · ") || null;
}

function taskTransition(p: Record<string, unknown>): string | null {
  const name = str(p.task_name);
  const oldS = str(p.old_status);
  const newS = str(p.new_status);
  const transition = oldS && newS
    ? `${TASK_STATUS_NB[oldS] ?? oldS} → ${TASK_STATUS_NB[newS] ?? newS}`
    : null;
  const reason = str(p.rejection_reason);
  return [name, transition, reason ? `«${reason}»` : null].filter(Boolean).join(" · ") || null;
}

/** Tildeling: hvem + timer (snapshot-payload). */
function assignmentDetail(p: Record<string, unknown>): string | null {
  const hours = num(p.hours) ?? num((p.new as Record<string, unknown> | undefined)?.hours);
  const label = str(p.label);
  return [hours != null ? formatHours(hours) : null, label].filter(Boolean).join(" · ") || null;
}

const ABSENCE_TYPE_NB: Record<string, string> = {
  sick: "Sykdom", vacation: "Ferie", avspasering: "Avspasering",
};

/**
 * Fravær: type + periode. Leser både flat payload (frontend `logEvent` ved
 * opprettelse) og snapshot-formene fra DB-triggerne — `{old, new}` ved update,
 * `to_jsonb(OLD)` ved delete (20260625080050_absence_change_capture.sql).
 *
 * `note` gjengis ALDRI: sykefravær kan bære helseopplysninger, og loggen leses
 * bredere enn selve registreringen. Samme avgrensning som fraværsvarslene.
 */
function absenceDetail(p: Record<string, unknown>): string | null {
  const nested = (p.new ?? p.old) as Record<string, unknown> | undefined;
  const src = str(p.type) ? p : (nested ?? p);
  const type = str(src.type);
  const start = str(src.start_date);
  const end = str(src.end_date);
  const startTime = str(src.start_time);
  const endTime = str(src.end_time);

  const label = type ? (ABSENCE_TYPE_NB[type] ?? type) : null;
  let period: string | null = null;
  if (start) {
    period = start === end || !end ? start : `${start}–${end}`;
    if (startTime && endTime) {
      period += ` ${startTime.slice(0, 5)}–${endTime.slice(0, 5)}`;
    }
  }
  return [label, period].filter(Boolean).join(" · ") || null;
}

/** Norske feltnavn for kundeinfo-events (VIB-2531). */
/** Rollenivå slik de heter i UI, til tilgangsendringer på mapper (VIB-2703). */
const ROLE_NB: Record<string, string> = {
  user: "Ansatt",
  admin: "Admin · Leder",
  superadmin: "Superadmin",
};

const CUSTOMER_FIELD_NB: Record<string, string> = {
  contact_person: "kontaktperson",
  contact_email: "e-post",
  invoice_email_address: "faktura-e-post",
  invoice_email_cc: "faktura-kopi",
  contact_phone: "telefon",
  address: "adresse",
  postal_code: "postnummer",
  city: "poststed",
};

export const EVENT_TYPES: Record<string, EventTypeDef> = {
  // Dokumentarkivet (VIB-2703). Skrevet av DB-triggerne log_document_change
  // og log_document_folder_change.
  document_created: {
    label: "Dokument lagt til", color: GREEN, filter: "dokumenter",
    format: (p) => documentLine(p),
  },
  document_updated: {
    label: "Dokument endret", color: BLUE, filter: "dokumenter",
    format: (p) => documentLine(p),
  },
  document_deleted: {
    label: "Dokument slettet", color: RED, filter: "dokumenter",
    // Ingen snapshot å lese fra, og det er med vilje: sletting er soft, så
    // raden ligger igjen med tittelen sin bak mappas tilgangsnivå.
    format: (p) => documentLine(p),
  },
  // Medredaktører (VIB-2722). Skrevet av DB-triggeren log_document_editor_change.
  document_editor_added: {
    label: "Fikk redigeringstilgang", color: GREEN, filter: "dokumenter",
    // Som resten av arkivet: ingen tittel i loggen. event_log er lesbar for
    // alle innloggede, dokumentet er bak mappas tilgangsnivå. Hvem det gjelder
    // er et bruker-id, ikke innhold, og det kan slås opp av den som har
    // tilgang til å se brukerlista.
    format: (p) => (str(p.user_id) ? "én person" : null),
  },
  document_editor_removed: {
    label: "Mistet redigeringstilgang", color: AMBER, filter: "dokumenter",
    format: (p) => (str(p.user_id) ? "én person" : null),
  },
  document_folder_created: {
    label: "Mappe opprettet", color: GREEN, filter: "dokumenter",
    format: (p) => folderAccessLine(p),
  },
  document_folder_access_changed: {
    label: "Mappetilgang endret", color: AMBER, filter: "dokumenter",
    // Rollekravene før og etter står eksplisitt i payloaden, ikke i et
    // snapshot, nettopp fordi snapshotet også ville båret mappenavnet.
    format: (p) => {
      const fra = ROLE_NB[str(p.old_read_min_role) ?? ""] ?? str(p.old_read_min_role);
      const til = ROLE_NB[str(p.read_min_role) ?? ""] ?? str(p.read_min_role);
      if (fra && til && fra !== til) return `lesetilgang ${fra} → ${til}`;
      return folderAccessLine(p);
    },
  },
  document_folder_deleted: {
    label: "Mappe slettet", color: RED, filter: "dokumenter",
    format: (p) => folderAccessLine(p),
  },
  // Grupper og mappetildelinger (VIB-2726). Skrevet av DB-triggerne
  // log_access_group_change, log_access_group_member_change og
  // log_document_folder_grant_change.
  //
  // Filter-kategorien er «dokumenter» selv om en gruppe i prinsippet er
  // domenenøytral: arkivet er i dag det ENESTE som bruker grupper. Tas de i
  // bruk på prosjekter eller rapporter, får de sin egen kategori da — en
  // kategori uten events i er bare en død valgmulighet i filteret.
  access_group_created: {
    label: "Gruppe opprettet", color: GREEN, filter: "dokumenter",
    format: (p) => str(p.name),
  },
  access_group_renamed: {
    label: "Gruppe endret navn", color: BLUE, filter: "dokumenter",
    format: (p) => {
      const fra = str(p.old_name);
      const til = str(p.name);
      return fra && til ? `${fra} → ${til}` : til;
    },
  },
  access_group_deleted: {
    label: "Gruppe slettet", color: RED, filter: "dokumenter",
    // Snapshotet er hele poenget: loggen skal si hvem som mistet tilgang, og
    // etter sletting finnes ikke svaret andre steder.
    format: groupDeletedLine,
  },
  access_group_member_added: {
    label: "Lagt til i gruppe", color: GREEN, filter: "dokumenter",
    format: groupMemberLine,
  },
  access_group_member_removed: {
    label: "Fjernet fra gruppe", color: AMBER, filter: "dokumenter",
    format: groupMemberLine,
  },
  document_folder_grant_added: {
    label: "Mappe delt med gruppe", color: GREEN, filter: "dokumenter",
    format: folderGrantLine,
  },
  document_folder_grant_changed: {
    label: "Gruppas mappetilgang endret", color: AMBER, filter: "dokumenter",
    format: (p) => {
      const fra = grantLevel(p.old_can_read, p.old_can_write);
      const til = grantLevel(p.can_read, p.can_write);
      const group = str(p.group_name);
      if (fra && til && fra !== til) {
        return [group, `${fra} → ${til}`].filter(Boolean).join(" · ");
      }
      return folderGrantLine(p);
    },
  },
  document_folder_grant_removed: {
    label: "Mappedeling fjernet", color: RED, filter: "dokumenter",
    format: folderGrantLine,
  },
  // Timer
  time_entry_created: {
    label: "Timeregistrering", color: GRAY, filter: "time",
    format: (p) => {
      const h = num(p.hours);
      const desc = str(p.task_description);
      if (h == null) return desc;
      return `${formatHours(h)}${desc ? " — " + desc : ""}`;
    },
  },
  // Fravær (VIB-1653) — event_log-typer som fantes uten presentasjon:
  // `absences.created` fra useCreateAbsence, `absences.updated`/`.deleted` fra
  // triggerne i 20260625080050_absence_change_capture.sql.
  "absences.created": {
    label: "Fravær registrert", color: BLUE, filter: "time", format: absenceDetail,
  },
  "absences.updated": {
    label: "Fravær endret", color: AMBER, filter: "time", format: absenceDetail,
  },
  "absences.deleted": {
    label: "Fravær slettet", color: RED, filter: "time", format: absenceDetail,
  },
  // Timebank (VIB-2766). Skrevet av DB-triggeren log_timebank_delete, som
  // fanger den HARDE slettingen når en ansatt trekker sin egen søknad.
  //
  // Detaljlinja sier timer og type, men IKKE hvem. `event_log` har ingen RLS
  // og er lesbar for enhver innlogget bruker (VIB-2738), og hvor mange
  // avspaseringstimer en navngitt kollega hadde er ikke allemannseie. Hvem det
  // gjelder ligger som bruker-id i payload, slik resten av arkivet gjør det,
  // og kan slås opp av den som faktisk skal se det.
  timebank_transaction_deleted: {
    label: "Timebank-søknad trukket", color: RED, filter: "time",
    format: (p) => {
      const timer = num(p.hours);
      const type = str(p.kind) === "withdrawal" ? "uttak" : "oppsparing";
      return timer === null ? type : `${type}, ${formatHours(timer)}`;
    },
  },
  // Kommentarer
  comment_added: {
    label: "Kommentar lagt til", color: GRAY, filter: "comments",
    format: (p) => str(p.body_preview),
  },
  // Oppgaver
  task_updated: { label: "Oppgave oppdatert", color: GRAY, filter: "tasks", format: taskTransition },
  task_completed: { label: "Oppgave fullført", color: BLUE, filter: "tasks", format: taskTransition },
  task_approved: { label: "Oppgave godkjent", color: GREEN, filter: "tasks", format: taskTransition },
  task_rejected: { label: "Oppgave avvist", color: RED, filter: "tasks", format: taskTransition },
  // Tildelinger (budget_line_assignments)
  assignment_created: { label: "Tildeling opprettet", color: GRAY, filter: "tasks", format: assignmentDetail },
  assignment_updated: { label: "Tildeling endret", color: GRAY, filter: "tasks", format: assignmentDetail },
  assignment_removed: { label: "Tildeling fjernet", color: RED, filter: "tasks", format: assignmentDetail },
  // Budsjett
  budget_line_added: {
    label: "Budsjettlinje opprettet", color: GRAY, filter: "budget",
    format: (p) => str(p.description),
  },
  budget_line_updated: {
    label: "Budsjettlinje oppdatert", color: GRAY, filter: "budget",
    format: (p) => {
      const desc = str(p.description);
      const oh = num(p.old_hours); const nh = num(p.new_hours);
      const oa = num(p.old_amount); const na = num(p.new_amount);
      const parts: string[] = [];
      if (desc) parts.push(desc);
      if (oh !== nh) parts.push(`${formatHours(oh ?? 0)} → ${formatHours(nh ?? 0)}`);
      if (oa !== na) parts.push(`${formatNOK(oa ?? 0)} → ${formatNOK(na ?? 0)}`);
      return parts.join(" · ") || null;
    },
  },
  budget_line_removed: {
    label: "Budsjettlinje fjernet", color: RED, filter: "budget",
    format: (p) => str(p.description),
  },
  // VIB-2927: frilanser på ekstern budsjettlinje (trigger på budget_line_freelancers).
  // Ved cascade fra en slettet linje er description null.
  budget_line_freelancer_added: {
    label: "Frilanser satt på linje", color: GREEN, filter: "budget",
    format: (p) => [str(p.freelancer_name), str(p.description)].filter(Boolean).join(" · ") || null,
  },
  budget_line_freelancer_removed: {
    label: "Frilanser fjernet fra linje", color: GRAY, filter: "budget",
    format: (p) => [str(p.freelancer_name), str(p.description)].filter(Boolean).join(" · ") || null,
  },
  // VIB-1202: samlende revisjon av godkjent hovedbudsjett (frontend-logget).
  // Per-linje-endringene logges autoritativt av DB-triggerne
  // (budget_line_added/updated/removed); dette eventet bærer begrunnelsen og
  // sum-effekten på tvers av linjene.
  budget_revised: {
    label: "Budsjett revidert", color: AMBER, filter: "budget",
    format: (p) => {
      const reason = str(p.reason);
      const tb = num(p.total_before); const ta = num(p.total_after);
      const parts: string[] = [];
      const nChanges =
        (num(p.updated_count) ?? 0) + (num(p.created_count) ?? 0) + (num(p.deleted_count) ?? 0);
      if (nChanges > 0) parts.push(`${nChanges} linjeendringer`);
      if (tb != null && ta != null && tb !== ta) {
        parts.push(`Sum: ${formatNOK(tb)} → ${formatNOK(ta)}`);
      }
      const head = parts.join(" · ");
      return [head || null, reason].filter(Boolean).join(" — ") || null;
    },
  },
  // Faktura
  invoice_created: {
    label: "Faktura opprettet", color: PURPLE, filter: "invoices",
    format: (p) => { const a = num(p.amount); return a != null ? `Beløp: ${formatNOK(a)}` : null; },
  },
  invoice_paid: {
    label: "Faktura betalt", color: GREEN, filter: "invoices",
    format: (p) => { const a = num(p.amount); return a != null ? `Beløp: ${formatNOK(a)}` : null; },
  },
  // VIB-2836: trg_log_project_invoice_terms_change — gammel og ny faktureringsplan.
  invoice_terms_changed: {
    label: "Faktureringsplan endret", color: AMBER, filter: "invoices",
    format: (p) => {
      const o = formatTerms(p.old_terms); const n = formatTerms(p.new_terms);
      return o && n ? `${o} → ${n}` : null;
    },
  },
  // VIB-2836: engangs-backfill upsell → interim for terminfakturaer (event_log-snapshot).
  "invoices.type_backfilled": {
    label: "Fakturatype rettet til mellomfaktura", color: GRAY, filter: "invoices",
    format: () => null,
  },
  // Manuell inntekt (VIB-792) — superadmin-registrerte inntektsrader uten PO-faktura
  manual_revenue_added: {
    label: "Manuell inntekt registrert", color: PURPLE, filter: "invoices",
    format: (p) => {
      const a = num(p.amount_excl_vat);
      const d = str(p.description);
      return [a != null ? formatNOK(a) : null, d].filter(Boolean).join(" — ") || null;
    },
  },
  manual_revenue_updated: {
    label: "Manuell inntekt endret", color: AMBER, filter: "invoices",
    format: (p) => {
      const oa = num(p.old_amount_excl_vat); const na = num(p.amount_excl_vat);
      const d = str(p.description);
      if (oa != null && na != null && oa !== na) {
        return `${formatNOK(oa)} → ${formatNOK(na)}${d ? " — " + d : ""}`;
      }
      return [na != null ? formatNOK(na) : null, d].filter(Boolean).join(" — ") || null;
    },
  },
  manual_revenue_deleted: {
    label: "Manuell inntekt slettet", color: RED, filter: "invoices",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      const a = num(old.amount_excl_vat) ?? num(p.amount_excl_vat);
      const d = str(old.description) ?? str(p.description);
      return [a != null ? formatNOK(a) : null, d].filter(Boolean).join(" — ") || null;
    },
  },
  // Bilag
  voucher_added: {
    label: "Bilag lagt til", color: GRAY, filter: "vouchers",
    format: (p) => {
      const supplier = str(p.supplier_name);
      const a = num(p.amount);
      return [supplier, a != null ? formatNOK(a) : null].filter(Boolean).join(" — ") || null;
    },
  },
  voucher_approved: {
    label: "Bilag godkjent", color: GREEN, filter: "vouchers",
    format: (p) => {
      const nr = str(p.voucher_number);
      const c = str(p.comment);
      return [nr ? `Bilag ${nr}` : null, c].filter(Boolean).join(" · ") || null;
    },
  },
  voucher_rejected: {
    label: "Bilag avvist", color: RED, filter: "vouchers",
    format: (p) => {
      const nr = str(p.voucher_number);
      const c = str(p.comment);
      return [nr ? `Bilag ${nr}` : null, c].filter(Boolean).join(" · ") || null;
    },
  },
  // Møter
  meeting_completed: {
    label: "Møte gjennomført", color: GRAY, filter: "meetings",
    format: (p) => str(p.description),
  },
  // Prosjekt
  project_status_changed: {
    label: "Prosjektstatus endret", color: GRAY, filter: "project",
    format: (p) => {
      const o = str(p.old_value); const n = str(p.new_value);
      return o && n ? `${PROJECT_STATUS_NB[o] ?? o} → ${PROJECT_STATUS_NB[n] ?? n}` : null;
    },
  },
  phase_changed: {
    label: "Fase endret", color: GRAY, filter: "project",
    format: (p) => {
      const o = str(p.old_value); const n = str(p.new_value);
      return o && n ? `${PHASE_NB[o] ?? o} → ${PHASE_NB[n] ?? n}` : null;
    },
  },
  project_renamed: {
    label: "Prosjektnavn endret", color: GRAY, filter: "project",
    format: (p) => `${str(p.old_name) ?? "—"} → ${str(p.new_name) ?? "—"}`,
  },
  // Økonomi
  commission_changed: {
    label: "Provisjon endret", color: AMBER, filter: "okonomi",
    format: (p) => {
      const oPct = num(p.old_ownership_pct); const nPct = num(p.new_ownership_pct);
      if (oPct !== nPct) return `Provisjon: ${oPct ?? "—"}% → ${nPct ?? "—"}%`;
      const oFix = num(p.old_fixed_amount); const nFix = num(p.new_fixed_amount);
      if (oFix !== nFix) return `Fastbeløp: ${oFix != null ? formatNOK(oFix) : "—"} → ${nFix != null ? formatNOK(nFix) : "—"}`;
      if (p.old_split_type !== p.new_split_type) return `Split: ${str(p.old_split_type) ?? "—"} → ${str(p.new_split_type) ?? "—"}`;
      return p.action === "DELETE" ? "Eierandel fjernet" : "Eierandel lagt til";
    },
  },
  upsell_changed: {
    label: "Mersalg endret", color: PURPLE, filter: "okonomi",
    format: (p) => {
      const parts: string[] = [];
      if (p.old_status !== p.new_status) parts.push(`Status: ${str(p.old_status) ?? "—"} → ${str(p.new_status) ?? "—"}`);
      const oa = num(p.old_amount); const na = num(p.new_amount);
      if (oa !== na) parts.push(`Beløp: ${oa != null ? formatNOK(oa) : "—"} → ${na != null ? formatNOK(na) : "—"}`);
      if (parts.length) return parts.join(" · ");
      return str(p.description) ?? "Mersalg opprettet";
    },
  },
  // VIB-994: faktureringsform-bytte (fixed ↔ tm), frontend-logget
  // (use-upsell-mutations.ts) fordi valget kun kan gjøres mens mersalget er
  // tomt — modus-lås-vernet er dekket i mutation-laget, ikke en DB-trigger.
  upsell_billing_mode_changed: {
    label: "Mersalg: faktureringsform endret", color: AMBER, filter: "okonomi",
    format: (p) => {
      const o = str(p.old_billing_mode); const n = str(p.new_billing_mode);
      const label = (v: string | null) => (v === "tm" ? "Medgått tid + materiell" : v === "fixed" ? "Fastpris" : v ?? "—");
      return `${label(o)} → ${label(n)}`;
    },
  },
  voucher_allocation_changed: {
    label: "Bilag-allokering endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const a = num(p.new_amount) ?? num(p.old_amount);
      return a != null ? `Beløp: ${formatNOK(a)}` : null;
    },
  },
  project_lead_changed: {
    label: "Prosjektleder endret", color: INDIGO, filter: "okonomi",
    format: (p) => `${str(p.old_name) ?? "—"} → ${str(p.new_name) ?? "—"}`,
  },
  advisor_changed: {
    label: "Rådgiver endret", color: INDIGO, filter: "okonomi",
    format: (p) => `${str(p.old_name) ?? "—"} → ${str(p.new_name) ?? "—"}`,
  },
  // Kontrakter (VIB-1281/1282)
  extra_contract_generated: {
    label: "Kontrakt generert", color: BLUE, filter: "okonomi",
    format: (p) => str(p.template_type),
  },
  extra_contract_sent: {
    label: "Kontrakt sendt til signering", color: BLUE, filter: "okonomi",
    format: () => null,
  },
  // Utbetaling av statister (VIB-2092)
  extra_ready_for_payment: {
    label: "Statist klar for utbetaling", color: BLUE, filter: "okonomi",
    format: (p) => str(p.extra_name),
  },
  extra_marked_paid: {
    label: "Statist markert utbetalt", color: GREEN, filter: "okonomi",
    format: (p) => str(p.extra_name),
  },
  extra_contract_signed: {
    label: "Kontrakt signert", color: GREEN, filter: "okonomi",
    format: (p) => str(p.signed_name),
  },
  // VIB-2099: medredigerere på tilbud. DB-trigger log_offer_editor_change.
  offer_editor_added: {
    label: "Medredigerer lagt til", color: BLUE, filter: "okonomi",
    format: (p) => str(p.user_id) ? `Bruker ${str(p.user_id)}` : null,
  },
  offer_editor_removed: {
    label: "Medredigerer fjernet", color: RED, filter: "okonomi",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      const uid = str(old.user_id) ?? str(p.user_id);
      return uid ? `Bruker ${uid}` : null;
    },
  },
  // VIB-2979: Grunnlag i tilbudsarbeidsrommet. DB-trigger log_offer_workspace_change.
  offer_workspace_updated: {
    label: "Tilbudsgrunnlag endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const changes = (p.changes ?? {}) as Record<string, unknown>;
      const fields = [
        "target_audience" in changes ? "målgruppe" : null,
        "budget_frame_nok" in changes ? "budsjettramme" : null,
        "ai_sources" in changes ? "AI-kilder" : null,
      ].filter(Boolean);
      return fields.length ? fields.join(", ") : null;
    },
  },
  offer_workspace_deleted: {
    label: "Tilbudsgrunnlag slettet", color: RED, filter: "okonomi",
    format: () => null,
  },
  // VIB-2980: Idé-tavlen i tilbudsarbeidsrommet. DB-triggere log_offer_idea_card_change
  // og log_offer_idea_group_change. Ren flytting logges ikke.
  offer_idea_card_changed: {
    label: "Idé-kort endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const kind = IDEA_KIND_LABEL[str(p.kind) as OfferIdeaCardKind] ?? "Kort";
      const changes = (p.changes ?? {}) as Record<string, unknown>;
      if (str(p.op) === "insert") return `${kind} lagt til`;
      if ("deleted_at" in changes) return changes.deleted_at ? `${kind} slettet` : `${kind} gjenopprettet`;
      const fields = [
        "body" in changes ? "tekst" : null,
        "url" in changes ? "lenke" : null,
        "storage_path" in changes ? "bilde" : null,
        "vimeo_id" in changes ? "film" : null,
        "group_id" in changes ? "gruppe" : null,
        "use_for_ai" in changes ? (changes.use_for_ai ? "brukes av AI" : "brukes ikke av AI") : null,
      ].filter(Boolean);
      return fields.length ? `${kind}: ${fields.join(", ")}` : kind;
    },
  },
  offer_idea_card_deleted: {
    label: "Idé-kort slettet med tilbudet", color: RED, filter: "okonomi",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      return IDEA_KIND_LABEL[str(old.kind) as OfferIdeaCardKind] ?? null;
    },
  },
  offer_idea_group_changed: {
    label: "Idé-gruppe endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const changes = (p.changes ?? {}) as Record<string, unknown>;
      if ("deleted_at" in changes) return changes.deleted_at ? "Gruppe slettet" : "Gruppe gjenopprettet";
      return str(changes.label) ? `«${str(changes.label)}»` : null;
    },
  },
  offer_idea_group_deleted: {
    label: "Idé-gruppe slettet med tilbudet", color: RED, filter: "okonomi",
    format: () => null,
  },
  // Løpende avtaler (VIB-1291). Skrevet av DB-trigger log_recurring_agreement_change/
  // _deleted til event_log — kun de sjeldne tilfellene med project_id satt
  // (storage-avtaler, VIB-1308) dukker i tillegg opp i project_events/ProjectLogTab.
  "agreement.created": {
    label: "Avtale opprettet", color: PURPLE, filter: "okonomi",
    format: (p) => `${AGREEMENT_TYPE_NB[str(p.type) ?? ""] ?? str(p.type) ?? "Avtale"} · ${AGREEMENT_INTERVAL_NB[str(p.billing_interval) ?? ""] ?? "—"}`,
  },
  "agreement.updated": {
    label: "Avtale endret", color: GRAY, filter: "okonomi",
    format: (p) => str(p.next_run_date) ? `Neste kjøring: ${str(p.next_run_date)}` : null,
  },
  "agreement.paused": {
    label: "Avtale satt på pause", color: AMBER, filter: "okonomi",
    format: () => null,
  },
  "agreement.resumed": {
    label: "Avtale gjenopptatt", color: GREEN, filter: "okonomi",
    format: () => null,
  },
  "agreement.ended": {
    label: "Avtale avsluttet", color: RED, filter: "okonomi",
    format: () => null,
  },
  "agreement.deleted": {
    label: "Avtale slettet", color: RED, filter: "okonomi",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      const type = str(old.type);
      return type ? `${AGREEMENT_TYPE_NB[type] ?? type} slettet` : null;
    },
  },
  // Fakturamotor (VIB-1292/1293). Skrives til event_log (source=system) av
  // run-recurring-invoices — én oppsummeringsrad per kjøring + én per feil.
  recurring_invoice_run: {
    label: "Fakturamotor kjørt", color: BLUE, filter: "invoices",
    format: (p) => {
      const parts: string[] = [];
      const created = num(p.created);
      const skipped = num(p.skipped);
      const failed = num(p.failed);
      if (created != null && created > 0) parts.push(`${created} opprettet`);
      if (skipped != null && skipped > 0) parts.push(`${skipped} hoppet over`);
      if (failed != null && failed > 0) parts.push(`${failed} feilet`);
      if (parts.length) return parts.join(" · ");
      const due = num(p.due);
      return due != null ? `${due} forfalte avtaler` : null;
    },
  },
  recurring_invoice_error: {
    label: "Fakturamotor-feil", color: RED, filter: "invoices",
    format: (p) => str(p.message) ?? str(p.error) ?? null,
  },
  // Auto-send-bryter (VIB-1293) — sikkerhets-/økonomi-event, ekte aktør.
  "config.autosend_toggled": {
    label: "Auto-send-bryter endret", color: AMBER, filter: "okonomi",
    format: (p) => (p.enabled === true ? "Skrudd PÅ" : p.enabled === false ? "Skrudd AV" : null),
  },
  "monthly_confirmations.attested": {
    label: "Måned lukket", color: GREEN, filter: "time",
    format: () => null,
  },
  "monthly_confirmations.reopened": {
    label: "Måned gjenåpnet", color: AMBER, filter: "time",
    format: (p) => str(p.reason),
  },
  "monthly_confirmations.deleted": {
    label: "Månedslukking slettet", color: RED, filter: "time",
    format: () => null,
  },
  // Kundeinfo endret (VIB-2531). Skrives av update-po-customer, som pusher til
  // PowerOffice før den lagrer lokalt. Logges også når PO AVVISER endringen,
  // derfor `ok`-flagget — et mislykket forsøk på å endre data som går videre
  // til regnskap er i seg selv verdt å kunne spore.
  customer_updated: {
    label: "Kundeinfo endret", color: AMBER, filter: "okonomi",
    format: (p) => {
      const name = str(p.customer_name);
      const fields = Array.isArray(p.fields)
        ? (p.fields as unknown[]).filter((f): f is string => typeof f === "string")
        : [];
      const labels = fields.map((f) => CUSTOMER_FIELD_NB[f] ?? f);
      if (p.ok === false) {
        const err = str(p.error);
        return [name, "avvist", err].filter(Boolean).join(" · ");
      }
      return [name, labels.join(", ")].filter(Boolean).join(" · ") || null;
    },
  },
  customer_created: {
    label: "Kunde opprettet", color: GRAY, filter: "okonomi",
    format: (p) => {
      const name = str(p.name);
      const country = str(p.country_code);
      const org = str(p.organization_number);
      const parts = [name];
      if (country && country !== "NO") parts.push(country);
      if (org) parts.push(org);
      return parts.filter(Boolean).join(" · ") || null;
    },
  },
  customer_po_linked: {
    label: "Kunde koblet til PowerOffice", color: BLUE, filter: "okonomi",
    format: (p) => {
      const name = str(p.name);
      const method = str(p.method);
      return [name, method].filter(Boolean).join(" · ") || null;
    },
  },
  customer_po_link_failed: {
    label: "PowerOffice-kobling feilet", color: RED, filter: "okonomi",
    format: (p) => {
      const name = str(p.name);
      const reason = str(p.error);
      return [name, reason].filter(Boolean).join(" · ") || null;
    },
  },
  // VIB-2575: PO-prosjekt opprettet etter kunde-kobling, eller feilet ved
  // prosjektopprettelse/kobling. Uten PO-prosjekt kan prosjektet ikke faktureres.
  project_po_created: {
    label: "Prosjekt opprettet i PowerOffice", color: BLUE, filter: "okonomi",
    format: (p) => {
      const code = str(p.project_code);
      const customer = str(p.customer_name);
      return [code, customer].filter(Boolean).join(" · ") || null;
    },
  },
  // Staging-refresh (VIB-2884). Skrives av scripts/staging/pseudonymise.sql
  // (source=system) som kvittering på at databasen er en kopi av prod.
  staging_refresh: {
    label: "Staging fylt fra prod", color: GRAY, filter: "project",
    format: (p) => {
      const at = str(p.refreshed_at);
      return at ? `Pseudonymisert kopi av prod, ${at.slice(0, 10)}` : "Pseudonymisert kopi av prod";
    },
  },
  project_po_create_failed: {
    label: "Prosjekt ikke opprettet i PowerOffice", color: RED, filter: "okonomi",
    format: (p) => {
      const code = str(p.project_code);
      const reason = str(p.error);
      return [code, reason].filter(Boolean).join(" · ") || null;
    },
  },
  // CRM-kontaktpersoner (CRM-1.1). crm_contact_* skrives av frontend-hooken,
  // customer_contact.* av DB-triggeren log_customer_contact_change (aldri e-post/telefon).
  crm_contact_created: {
    label: "Kontaktperson opprettet", color: GRAY, filter: "okonomi",
    format: (p) => [str(p.full_name), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  crm_contact_updated: {
    label: "Kontaktperson endret", color: BLUE, filter: "okonomi",
    format: (p) => [str(p.full_name), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  crm_contact_deactivated: {
    label: "Kontaktperson satt inaktiv", color: GRAY, filter: "okonomi",
    format: (p) => [str(p.full_name), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  crm_contact_confirmed: {
    label: "Kontaktperson bekreftet", color: BLUE, filter: "okonomi",
    format: (p) => [str(p.full_name), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  crm_contact_anonymized: {
    label: "Person slettet", color: GRAY, filter: "okonomi",
    format: (p) => str(p.customer_name),
  },
  "customer_contact.created": {
    label: "Kontaktperson opprettet", color: GRAY, filter: "okonomi",
    format: (p) => str(p.full_name),
  },
  "customer_contact.updated": {
    label: "Kontaktperson endret", color: BLUE, filter: "okonomi",
    format: (p) => str(p.full_name),
  },
  "customer_contact.deactivated": {
    label: "Kontaktperson satt inaktiv", color: GRAY, filter: "okonomi",
    format: (p) => str(p.full_name),
  },
  "customer_contact.anonymized": {
    label: "Person slettet", color: GRAY, filter: "okonomi",
    format: () => null,
  },
  // Frilanserbanken (VIB-2925), skrevet av DB-triggerne i 20261024152206_vib2925_freelancer_bank.sql.
  // Payloadene har aldri e-post, telefon, notater, kommentartekst eller filnavn.
  "freelancer.created": { label: "Frilanser opprettet", color: GREEN, filter: "project", format: (p) => str(p.name) },
  "freelancer.updated": { label: "Frilanser endret", color: BLUE, filter: "project", format: (p) => str(p.name) },
  "freelancer.archived": { label: "Frilanser arkivert", color: GRAY, filter: "project", format: (p) => str(p.name) },
  "freelancer.restored": { label: "Frilanser gjenopprettet", color: GREEN, filter: "project", format: (p) => str(p.name) },
  "freelancer.erased": { label: "Frilanser slettet permanent", color: RED, filter: "project", format: () => null },
  "freelancer.discipline_added": { label: "Fagfelt lagt til", color: BLUE, filter: "project", format: (p) => str(p.discipline) },
  "freelancer.discipline_removed": { label: "Fagfelt fjernet", color: GRAY, filter: "project", format: (p) => str(p.discipline) },
  "freelancer_review.added": { label: "Frilanser vurdert", color: GREEN, filter: "project", format: (p) => (num(p.rating) != null ? `${p.rating} av 5` : null) },
  "freelancer_review.updated": { label: "Vurdering endret", color: BLUE, filter: "project", format: (p) => (num(p.rating) != null ? `${p.rating} av 5` : null) },
  "freelancer_review.deleted": { label: "Vurdering slettet", color: GRAY, filter: "project", format: () => null },
  "freelancer_comment.added": { label: "Kommentar på frilanser", color: INDIGO, filter: "comments", format: () => null },
  "freelancer_comment.deleted": { label: "Kommentar på frilanser slettet", color: GRAY, filter: "comments", format: () => null },
  "freelancer_file.added": { label: "Fil lagt ved frilanser", color: INDIGO, filter: "dokumenter", format: (p) => str(p.file_extension) },
  "freelancer_file.deleted": { label: "Fil fjernet fra frilanser", color: GRAY, filter: "dokumenter", format: () => null },
  // VIB-2934: kontaktpersoner (payload: navn og rolle, aldri e-post eller telefon).
  "freelancer_contact.added": { label: "Kontaktperson lagt til frilanser", color: GREEN, filter: "project", format: (p) => str(p.name) },
  "freelancer_contact.updated": { label: "Kontaktperson på frilanser endret", color: BLUE, filter: "project", format: (p) => str(p.name) },
  "freelancer_contact.deleted": { label: "Kontaktperson fjernet fra frilanser", color: GRAY, filter: "project", format: (p) => str(p.name) },
  "freelancer_discipline.created": { label: "Fagfelt opprettet", color: GREEN, filter: "project", format: (p) => str(p.label) },
  "freelancer_discipline.updated": { label: "Fagfelt endret", color: BLUE, filter: "project", format: (p) => str(p.label) },
  // VIB-2927: leverandør ↔ frilanser (20261024155732_vib2927_freelancers_on_projects.sql).
  "freelancer_supplier.linked": { label: "Leverandør koblet til frilanser", color: GREEN, filter: "project", format: (p) => str(p.supplier_name) },
  "freelancer_supplier.not_freelancer": { label: "Leverandør merket ikke frilanser", color: GRAY, filter: "project", format: (p) => str(p.supplier_name) },
  "freelancer_supplier.unlinked": { label: "Leverandørkobling fjernet", color: GRAY, filter: "project", format: (p) => str(p.supplier_name) },
  // Kundeeiere (CRM-1.2). customer_owners.changed skrives av RPC set_customer_owners
  // med navnelister; customer_owner.* av audit-triggeren ved direkte DML,
  // customer_owners.deleted av snapshot-triggeren (payload.old = hele raden).
  "customer_owners.changed": {
    label: "Kundeeiere endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const names = (v: unknown) =>
        Array.isArray(v) ? v.filter((n): n is string => typeof n === "string").join(", ") : "";
      const next = names(p.new_names) || "ingen";
      const prev = names(p.previous_names);
      return prev ? `Eiere: ${prev} → ${next}` : `Eiere: ${next}`;
    },
  },
  "customer_owner.created": {
    label: "Kundeeier lagt til", color: GRAY, filter: "okonomi",
    format: () => null,
  },
  "customer_owner.updated": {
    label: "Kundeeier endret", color: GRAY, filter: "okonomi",
    format: () => null,
  },
  "customer_owners.deleted": {
    label: "Kundeeier fjernet", color: GRAY, filter: "okonomi",
    format: () => null,
  },
  // Prosesser i pipen (CRM-2.1). Skrives av DB-triggeren log_opportunity_change
  // (opportunity.*) og snapshot-triggeren (opportunities.deleted, payload.old = hele raden).
  "opportunity.created": {
    label: "Prosess opprettet", color: BLUE, filter: "okonomi",
    format: (p) => [str(p.title), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  "opportunity.stage_changed": {
    label: "Prosess flyttet", color: BLUE, filter: "okonomi",
    format: (p) => {
      const from = STAGE_LABELS[str(p.old_stage) as OpportunityStage];
      const to = STAGE_LABELS[str(p.new_stage) as OpportunityStage];
      return from && to ? `${from} → ${to}` : null;
    },
  },
  "opportunity.won": {
    label: "Prosess vunnet", color: GREEN, filter: "okonomi",
    format: (p) => [str(p.title), str(p.customer_name)].filter(Boolean).join(" · ") || null,
  },
  "opportunity.lost": {
    label: "Prosess tapt", color: RED, filter: "okonomi",
    format: (p) => {
      const reason = str(p.lost_reason);
      const title = str(p.title);
      return [title, reason ? `Årsak: ${reason}` : null].filter(Boolean).join(" · ") || null;
    },
  },
  "opportunity.updated": {
    label: "Prosess endret", color: BLUE, filter: "okonomi",
    format: (p) => str(p.title),
  },
  "opportunities.deleted": {
    label: "Prosess slettet", color: RED, filter: "okonomi",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).title),
  },
  // Oppfølginger (CRM-2.2). Skrives av DB-triggeren log_follow_up_change og
  // snapshot_follow_up_on_delete; aldri utkast, beskrivelse eller notat.
  "follow_up.created": {
    label: "Oppfølging opprettet", color: BLUE, filter: "okonomi",
    format: (p) => {
      const title = str(p.title);
      const rule = str(p.rule_key);
      if (!title) return null;
      const ruleLabel = isFollowUpRuleKey(rule) ? FOLLOW_UP_RULES[rule].label : rule;
      return ruleLabel ? `Oppfølging: ${title} (${ruleLabel})` : `Oppfølging: ${title}`;
    },
  },
  "follow_up.closed": {
    label: "Oppfølging lukket", color: GREEN, filter: "okonomi",
    format: (p) => {
      const outcome = FOLLOW_UP_OUTCOME_NB[str(p.outcome) ?? ""];
      return [str(p.title), outcome ? `Utfall: ${outcome}` : null].filter(Boolean).join(" · ") || null;
    },
  },
  "follow_up.postponed": {
    label: "Oppfølging utsatt", color: AMBER, filter: "okonomi",
    format: (p) => {
      const due = str(p.due_date);
      return [str(p.title), due ? `til ${due}` : null].filter(Boolean).join(" · ") || null;
    },
  },
  "follow_up.updated": {
    label: "Oppfølging endret", color: GRAY, filter: "okonomi",
    format: (p) => str(p.title),
  },
  "follow_up.deleted": {
    label: "Oppfølging slettet", color: RED, filter: "okonomi",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).title),
  },
  crm_follow_up_rules_run: {
    label: "Oppfølgingsregler kjørt", color: GRAY, filter: "okonomi",
    format: (p) => {
      const byRule = (p.created_by_rule ?? {}) as Record<string, unknown>;
      const total = Object.values(byRule).reduce<number>((sum, n) => sum + (typeof n === "number" ? n : 0), 0);
      return `${total} nye oppfølginger`;
    },
  },
  // Mailutkast (CRM-2.8): bare forbruk og id-er, aldri utkasttekst eller kundeinnhold.
  crm_draft_generated: {
    label: "Mailutkast laget", color: GRAY, filter: "okonomi",
    format: (p) => {
      const tokens = Number(p.input_tokens ?? 0) + Number(p.output_tokens ?? 0);
      return tokens > 0 ? `${tokens} tokens` : null;
    },
  },
  // Kontaktlogg og utboks (CRM-1.3). customer_activity.*/crm_outbox.* skrives av
  // DB-triggere (aldri e-postinnhold, mottaker eller meldingstekst), crm_activity_logged
  // av hooken ved manuell logging.
  "customer_activity.created": {
    label: "Kontaktlogg: ny aktivitet", color: GRAY, filter: "okonomi",
    format: (p) => ACTIVITY_TYPE_NB[str(p.type) ?? ""] ?? str(p.type),
  },
  "customer_activity.updated": {
    label: "Kontaktlogg: aktivitet endret", color: GRAY, filter: "okonomi",
    format: (p) => {
      const from = str(p.old_status);
      const to = str(p.status);
      return from && to && from !== to ? `${from} → ${to}` : null;
    },
  },
  "customer_activity.deleted": {
    label: "Kontaktlogg: aktivitet slettet", color: RED, filter: "okonomi",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      return ACTIVITY_TYPE_NB[str(old.type) ?? ""] ?? str(old.type);
    },
  },
  crm_activity_logged: {
    label: "Kontakt logget", color: BLUE, filter: "okonomi",
    format: (p) => {
      const kind = ACTIVITY_TYPE_NB[str(p.type) ?? ""] ?? str(p.type);
      const who = str(p.contact_name);
      return who ? `${kind} med ${who}` : kind;
    },
  },
  "crm_outbox.created": {
    label: "Utboks: melding planlagt", color: BLUE, filter: "okonomi",
    format: (p) => {
      const at = str(p.send_at);
      if (!at || Number.isNaN(Date.parse(at))) return null;
      return `Sendes ${formatSendTime(new Date(at))}`;
    },
  },
  "crm_outbox.updated": {
    label: "Utboks: status endret", color: GRAY, filter: "okonomi",
    format: (p) => (str(p.old_status) && str(p.status) ? `${str(p.old_status)} → ${str(p.status)}` : null),
  },
  "crm_outbox.deleted": {
    label: "Utboks: melding slettet", color: RED, filter: "okonomi",
    format: () => null,
  },
  // Kundestatus-overstyring (CRM-1.4). Skrevet av DB-triggeren log_customer_status_override.
  "customer.status_overridden": {
    label: "Kundestatus overstyrt", color: AMBER, filter: "okonomi",
    format: (p) => {
      const status = CUSTOMER_STATUS_NB[str(p.status) ?? ""] ?? str(p.status);
      return [str(p.customer_name), status, str(p.reason)].filter(Boolean).join(" · ") || null;
    },
  },
  "customer.status_override_cleared": {
    label: "Kundestatus-overstyring fjernet", color: GRAY, filter: "okonomi",
    format: (p) => str(p.customer_name),
  },
  // E-post via Gmail (CRM-1.5). Skrevet av gmail-send, gmail-sync og
  // crm-dispatch-outbox. Aldri e-postinnhold eller adresser i payloaden.
  crm_email_sent: {
    label: "E-post sendt", color: GREEN, filter: "okonomi",
    format: () => null,
  },
  crm_email_scheduled: {
    label: "E-post planlagt", color: BLUE, filter: "okonomi",
    format: (p) => {
      const at = str(p.send_at);
      if (!at || Number.isNaN(Date.parse(at))) return null;
      return `Sendes ${formatSendTime(new Date(at))}`;
    },
  },
  crm_email_received: {
    label: "E-post mottatt", color: BLUE, filter: "okonomi",
    format: () => null,
  },
  crm_outbox_failed: {
    label: "Utboks: sending feilet", color: RED, filter: "okonomi",
    format: (p) => [str(p.reason), num(p.attempts) != null ? `${num(p.attempts)} forsøk` : null].filter(Boolean).join(" · ") || null,
  },
  crm_gmail_sync_error: {
    label: "Gmail-synk feilet", color: AMBER, filter: "okonomi",
    format: (p) => str(p.reason),
  },
  crm_google_auth_error: {
    label: "Google-tilgang feilet", color: AMBER, filter: "okonomi",
    format: (p) => str(p.code),
  },
  // Signalagenter (CRM-3.0). signal_agent.* skrives av DB-triggeren log_signal_agent_change,
  // signal_agents.deleted av snapshot-triggeren (payload.old = hele raden).
  "signal_agent.created": {
    label: "Signalagent opprettet", color: BLUE, filter: "okonomi",
    format: (p) => str(p.name),
  },
  "signal_agent.updated": {
    label: "Signalagent endret", color: BLUE, filter: "okonomi",
    format: (p) => str(p.name),
  },
  "signal_agent.paused": {
    label: "Signalagent pauset", color: AMBER, filter: "okonomi",
    format: (p) => str(p.name),
  },
  "signal_agent.resumed": {
    label: "Signalagent aktivert", color: GREEN, filter: "okonomi",
    format: (p) => str(p.name),
  },
  "signal_agents.deleted": {
    label: "Signalagent slettet", color: RED, filter: "okonomi",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).name),
  },
  // Signaler (CRM-3.0). Payloaden har aldri overskrift, utkast eller bevis (kundeinnhold).
  "customer_signal.created": {
    label: "Signal opprettet", color: BLUE, filter: "okonomi",
    format: (p) => [str(p.signal_type), str(p.source)].filter(Boolean).join(" · ") || null,
  },
  "customer_signal.claimed": {
    label: "Signal tatt", color: GREEN, filter: "okonomi",
    format: (p) => str(p.signal_type),
  },
  "customer_signal.dismissed": {
    label: "Signal: avvist", color: GRAY, filter: "okonomi",
    format: (p) => str(p.signal_type),
  },
  "customer_signal.dismiss_reason": {
    label: "Signal: grunn for avvisning", color: GRAY, filter: "okonomi",
    format: (p) => [dismissReasonLabel(p.reason), str(p.signal_type)].filter(Boolean).join(" · ") || null,
  },
  "customer_signal.archived": {
    label: "Signal arkivert", color: GRAY, filter: "okonomi",
    format: (p) => str(p.signal_type),
  },
  "customer_signal.expired": {
    label: "Signaler utløpt", color: GRAY, filter: "okonomi",
    format: (p) => (num(p.count) != null ? `${num(p.count)} signaler` : null),
  },
  // Overvåkning (CRM-3.0). monitoring_source.updated skrives av hooken (med on_behalf_of),
  // monitoring_source.changed av DB-triggeren (også automatisk aktivering). Aldri adresse/handle.
  "monitoring_source.updated": {
    label: "Overvåkning endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const source = str(p.source);
      const before = p.before as Record<string, unknown> | undefined;
      const after = p.after as Record<string, unknown> | undefined;
      if (!source || !before || !after) return source;
      const onOff = (v: unknown) => (v === true ? "på" : "av");
      return `${source}: ${onOff(before.is_active)} → ${onOff(after.is_active)}`;
    },
  },
  "monitoring_source.changed": {
    label: "Overvåkning endret (automatisk logg)", color: GRAY, filter: "okonomi",
    format: (p) => str(p.source),
  },
  crm_agent_run_failed: {
    label: "Agentkjøring feilet", color: RED, filter: "okonomi",
    format: (p) => str(p.reason),
  },
  crm_agent_cost_cap_reached: {
    label: "Agent pauset: kostnadstak nådd", color: AMBER, filter: "okonomi",
    format: (p) => [str(p.agent_name), str(p.month)].filter(Boolean).join(" · ") || null,
  },
  // Lead-poolen (VIB-2985). Skrevet av edge function lead-pool ved påfyll fra lavvann.
  lead_pool_refill_started: {
    label: "Lead-pool: påfyll startet", color: BLUE, filter: "okonomi",
    format: (p) => (p.level != null ? `${str(p.level)} ringbare leads igjen` : null),
  },
  lead_pool_refill_skipped: {
    label: "Lead-pool: påfyll hoppet over", color: GRAY, filter: "okonomi",
    format: (p) => str(p.reason),
  },
  lead_pool_refill_failed: {
    label: "Lead-pool: påfyll feilet", color: RED, filter: "okonomi",
    format: (p) => [str(p.status), str(p.error_type)].filter(Boolean).join(" · ") || null,
  },
  // Kundelogo endret manuelt (VIB-2935). Skrevet av edge function customer-logo.
  customer_logo_changed: {
    label: "Kundelogo endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const action = { website: "hentet fra nettside", upload: "lastet opp", remove: "fjernet" }[str(p.action) ?? ""] ?? str(p.action);
      const site = str(p.website)?.replace(/^https?:\/\//, "").replace(/\/$/, "");
      return [str(p.customer_name), action, site].filter(Boolean).join(" · ") || null;
    },
  },
  crm_brreg_sync_run: {
    label: "Brønnøysund-synk kjørt", color: GRAY, filter: "okonomi",
    format: (p) => {
      const parts = [
        num(p.customers) != null ? `${num(p.customers)} kunder` : null,
        num(p.updated) != null ? `${num(p.updated)} oppdatert` : null,
        num(p.signals) != null ? `${num(p.signals)} signaler` : null,
        num(p.errors) ? `${num(p.errors)} feil` : null,
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : null;
    },
  },
  // Leads og møter (CRM-4.0). Skrevet av DB-triggere (lead_list.*, lead.*, meeting.*,
  // meeting_summary.*, user.host_flag_changed), RPC-en import_leads (lead.imported) og
  // edge functions (meeting.transcript_*, crm_sms_*). Payloaden har aldri kontaktdata,
  // notater, melding, referattekst eller transkripsjon.
  "lead_list.created": {
    label: "Leadliste opprettet", color: BLUE, filter: "okonomi",
    format: (p) => [str(p.name), str(p.source)].filter(Boolean).join(" · ") || null,
  },
  "lead_list.updated": {
    label: "Leadliste endret", color: BLUE, filter: "okonomi",
    format: (p) => str(p.name),
  },
  "lead.imported": {
    label: "Leads importert", color: GREEN, filter: "okonomi",
    format: (p) => {
      const parts = [
        num(p.inserted) != null ? `${num(p.inserted)} nye` : null,
        num(p.skipped_customer) ? `${num(p.skipped_customer)} allerede kunde` : null,
        num(p.skipped_duplicate) ? `${num(p.skipped_duplicate)} duplikater` : null,
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : null;
    },
  },
  "lead.claimed": { label: "Lead hentet", color: BLUE, filter: "okonomi", format: () => null },
  "lead.released": { label: "Lead frigitt", color: GRAY, filter: "okonomi", format: () => null },
  "lead.attempt_logged": {
    label: "Ringeforsøk logget", color: BLUE, filter: "okonomi",
    format: (p) => LEAD_OUTCOME_NB[str(p.outcome) ?? ""] ?? null,
  },
  // VIB-3032: RPC-en review_rejected_lead (vurdering av avviste).
  "lead.reviewed": {
    label: "Avvist lead vurdert", color: BLUE, filter: "okonomi",
    format: (p) => ({ requeue: "Tilbake i køen", postpone: "Utsatt", close: "Avsluttet for godt" } as Record<string, string>)[str(p.action) ?? ""] ?? null,
  },
  // VIB-3050: køen fylles fra poolene (crm_fill_queue), og admin endrer bookerens kø.
  "lead.assigned": {
    label: "Lead lagt i bookerkø", color: BLUE, filter: "okonomi",
    format: (p) => (str(p.reason) === "queue_fill" ? "Køen var under lavvann" : null),
  },
  "lead_queue.settings_changed": {
    label: "Bookerkø endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      const n = (p.new ?? {}) as Record<string, unknown>;
      const parts = [
        num(n.low_water) != null ? `lavvann ${num(n.low_water)}` : "lavvann standard",
        num(n.high_water) != null ? `høyvann ${num(n.high_water)}` : "høyvann standard",
        Array.isArray(n.pool_ids) ? `${n.pool_ids.length} pooler` : "alle pooler",
      ];
      return parts.join(", ");
    },
  },
  // VIB-3050 PR 3c: set_lead_booker i Leadlister.
  "lead_queue.booker_changed": {
    label: "Booker endret", color: BLUE, filter: "okonomi",
    format: (p) => {
      if (p.is_booker === true) return "Gjort til booker";
      const n = num(p.returned_to_pool) ?? 0;
      return n > 0 ? `Fjernet som booker, ${n} leads tilbake til poolen` : "Fjernet som booker";
    },
  },
  // VIB-3050 PR 3b: admin_lead_action i Leadlister (tildel, tilbake, flytt, avvis).
  "lead.admin_action": {
    label: "Lead endret av admin", color: BLUE, filter: "okonomi",
    format: (p) => ({ assign: "Tildelt booker", return: "Tilbake til poolen", move: "Flyttet til en annen pool", reject: "Avvist" } as Record<string, string>)[str(p.action) ?? ""] ?? null,
  },
  // VIB-3050: RPC-en return_lead_to_pool («Tilbake til poolen» på Leads-tavlen).
  "lead.returned_to_pool": {
    label: "Lead lagt tilbake i poolen", color: GRAY, filter: "okonomi",
    format: (p) => LEAD_OUTCOME_NB[str(p.last_outcome) ?? ""] ?? null,
  },
  "lead.converted": { label: "Lead ble kunde", color: GREEN, filter: "okonomi", format: () => null },
  "lead.anonymized": { label: "Lead anonymisert", color: AMBER, filter: "okonomi", format: () => null },
  // VIB-3020: RPC-en create_manual_lead («Nytt lead»).
  "lead.created_manual": {
    label: "Lead lagt inn manuelt", color: GREEN, filter: "okonomi",
    format: (p) => [str(p.name), p.reserved === true ? "reservert til den som la det inn" : null].filter(Boolean).join(" · ") || null,
  },
  "leads.deleted": {
    label: "Lead slettet", color: RED, filter: "okonomi",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).name),
  },
  "lead_lists.deleted": {
    label: "Leadliste slettet", color: RED, filter: "okonomi",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).name),
  },
  "meeting.booked": {
    label: "Møte booket", color: GREEN, filter: "okonomi",
    format: (p) => (str(p.start_at) ? formatDateTimeNb(str(p.start_at)!) : null),
  },
  "meeting.rescheduled": {
    label: "Møte flyttet", color: AMBER, filter: "okonomi",
    format: (p) => (str(p.start_at) ? `Ny tid ${formatDateTimeNb(str(p.start_at)!)}` : null),
  },
  "meeting.cancelled": { label: "Møte avlyst", color: RED, filter: "okonomi", format: () => null },
  "meeting.held": { label: "Møte gjennomført", color: GREEN, filter: "okonomi", format: () => null },
  "meeting.no_show": { label: "Kunden møtte ikke", color: RED, filter: "okonomi", format: () => null },
  "meeting.status_unknown": { label: "Møtestatus ukjent", color: AMBER, filter: "okonomi", format: () => null },
  "meeting.transcript_fetched": {
    label: "Transkripsjon hentet", color: BLUE, filter: "okonomi",
    format: (p) => (num(p.entry_count) != null ? `${num(p.entry_count)} innslag` : null),
  },
  "meeting.transcription_config_failed": {
    label: "Transkripsjon ble ikke slått på", color: AMBER, filter: "okonomi",
    format: (p) => (num(p.status) != null ? `Google svarte ${num(p.status)}` : null),
  },
  "meeting.transcript_purged": {
    label: "Transkripsjon slettet", color: GRAY, filter: "okonomi",
    format: (p) => (num(p.count) != null ? `${num(p.count)} transkripsjoner` : null),
  },
  "crm_meetings.deleted": {
    label: "Møte slettet", color: RED, filter: "okonomi",
    format: () => null,
  },
  "meeting_summary.generated": {
    label: "Møtereferat laget", color: BLUE, filter: "okonomi",
    format: (p) => (str(p.source) === "notes" ? "Fra notater" : "Fra transkripsjon"),
  },
  "meeting_summary.discarded": { label: "Møtereferat forkastet", color: RED, filter: "okonomi", format: () => null },
  "meeting_summary.regenerated": { label: "Møtereferat laget på nytt", color: BLUE, filter: "okonomi", format: () => null },
  "meeting_summary.approved": {
    label: "Møtereferat godkjent", color: GREEN, filter: "okonomi",
    format: (p) => (num(p.action_item_count) != null ? `${num(p.action_item_count)} avtalte punkter` : null),
  },
  "meeting_summaries.deleted": {
    label: "Møtereferat slettet", color: RED, filter: "okonomi",
    format: () => null,
  },
  "user.host_flag_changed": {
    label: "Kan bookes som vert endret", color: AMBER, filter: "okonomi",
    format: (p) => [str(p.user_name), p.new_value === true ? "på" : "av"].filter(Boolean).join(": ") || null,
  },
  crm_sms_sent: {
    label: "SMS sendt", color: GREEN, filter: "okonomi",
    format: (p) => REMINDER_KIND_NB[str(p.reminder_kind) ?? ""] ?? null,
  },
  crm_sms_failed: {
    label: "SMS feilet", color: RED, filter: "okonomi",
    format: (p) => str(p.reason),
  },
  // Utstyrsregisteret (VIB-2917). Skrevet av DB-triggere (equipment_item.*, equipment_service.*,
  // user.equipment_manager_changed, user.department_changed), importskriptet
  // (equipment_item.imported) og frontend (bilder). Payloaden har aldri beløp.
  // Bare bookingene (equipment_booking.*, VIB-2919) er prosjekt-scopet og vises i prosjektloggen.
  "equipment_item.created": {
    label: "Utstyr registrert", color: GREEN, filter: "utstyr",
    format: (p) => str(p.name),
  },
  "equipment_item.imported": {
    label: "Importert fra verktøyet", color: GREEN, filter: "utstyr",
    format: (p) => [str(p.source), str(p.in_use) ? `«I bruk?»: ${str(p.in_use)}` : null].filter(Boolean).join(" · ") || null,
  },
  "equipment_item.updated": {
    label: "Utstyr endret", color: BLUE, filter: "utstyr",
    format: (p) => {
      const changed = Array.isArray(p.changed) ? (p.changed as unknown[]).filter((c): c is string => typeof c === "string") : [];
      return changed.length > 0 ? changed.map((c) => EQUIPMENT_FIELD_NB[c] ?? c).join(", ") : null;
    },
  },
  "equipment_item.status_changed": {
    label: "Utstyrsstatus endret", color: AMBER, filter: "utstyr",
    format: (p) => [EQUIPMENT_STATUS_NB[str(p.old_status) ?? ""], EQUIPMENT_STATUS_NB[str(p.status) ?? ""]].filter(Boolean).join(" → ") || null,
  },
  "equipment_item.insurance_changed": {
    label: "Forsikring endret", color: BLUE, filter: "utstyr",
    format: (p) => p.insurance_declined === true
      ? "Ikke forny"
      : str(p.insured_until) ? `Forsikret til ${formatIsoDateNb(str(p.insured_until)!)}` : "Ikke forsikret",
  },
  "equipment_item.deleted": {
    label: "Utstyr slettet", color: RED, filter: "utstyr",
    format: (p) => str(p.name),
  },
  "equipment_items.deleted": {
    label: "Utstyr slettet permanent", color: RED, filter: "utstyr",
    format: (p) => str(((p.old ?? {}) as Record<string, unknown>).name),
  },
  // Lokasjonsbanken (VIB-2938). Skrevet av DB-triggere (location.*, location_image.*),
  // erase_location() og importskriptet (location.imported). Payload har aldri kontaktinfo.
  "location.created": { label: "Lokasjon lagt inn", color: GREEN, filter: "lokasjoner", format: (p) => str(p.name) },
  "location.imported": {
    label: "Importert fra verktøyet", color: GREEN, filter: "lokasjoner",
    format: (p) => [str(p.name), num(p.images) != null ? `${num(p.images)} bilder` : null].filter(Boolean).join(" · ") || null,
  },
  "location.updated": {
    label: "Lokasjon endret", color: BLUE, filter: "lokasjoner",
    format: (p) => {
      const changed = Array.isArray(p.changed) ? (p.changed as unknown[]).filter((c): c is string => typeof c === "string") : [];
      const names = [...new Set(changed.map((c) => LOCATION_FIELD_NB[c] ?? c))];
      return names.length > 0 ? names.join(", ") : null;
    },
  },
  "location.archived": { label: "Lokasjon arkivert", color: GRAY, filter: "lokasjoner", format: (p) => str(p.name) },
  "location.restored": { label: "Lokasjon gjenopprettet", color: BLUE, filter: "lokasjoner", format: (p) => str(p.name) },
  "location.erased": {
    label: "Lokasjon slettet for godt", color: RED, filter: "lokasjoner",
    format: (p) => [str(p.name), num(p.images) != null ? `${num(p.images)} bilder` : null].filter(Boolean).join(" · ") || null,
  },
  "location_image.added": { label: "Bilde lastet opp", color: BLUE, filter: "lokasjoner", format: () => null },
  "location_image.deleted": { label: "Bilde slettet", color: GRAY, filter: "lokasjoner", format: () => null },
  // VIB-2940: lokasjon på prosjekt. Skrevet av triggeren på project_locations,
  // både i project_events (prosjektets Logg) og event_log (lokasjonens historikk).
  "project_location.added": {
    label: "Lokasjon lagt på prosjekt", color: GREEN, filter: "lokasjoner",
    format: (p) => [str(p.location_name), projectRef(p)].filter(Boolean).join(" · ") || null,
  },
  "project_location.status_changed": {
    label: "Lokasjonsstatus endret", color: BLUE, filter: "lokasjoner",
    format: (p) => {
      const from = PROJECT_LOCATION_STATUS_NB[str(p.old_status) ?? ""];
      const to = PROJECT_LOCATION_STATUS_NB[str(p.status) ?? ""];
      const change = from && to ? `${from} → ${to}` : to ?? null;
      return [str(p.location_name), projectRef(p), change].filter(Boolean).join(" · ") || null;
    },
  },
  "project_location.removed": {
    label: "Lokasjon fjernet fra prosjekt", color: GRAY, filter: "lokasjoner",
    format: (p) => [str(p.location_name), projectRef(p)].filter(Boolean).join(" · ") || null,
  },
  "location_image.rotated": { label: "Bilde rotert", color: BLUE, filter: "lokasjoner", format: () => null },
  "location_image.moved": {
    label: "Bilde flyttet", color: BLUE, filter: "lokasjoner",
    format: (p) => {
      const other = str(p.other_name);
      if (!other) return null;
      return p.direction === "in" ? `fra ${other}` : `til ${other}`;
    },
  },
  "equipment_item.image_added": { label: "Bilde lastet opp", color: BLUE, filter: "utstyr", format: () => null },
  "equipment_item.image_removed": { label: "Bilde fjernet", color: GRAY, filter: "utstyr", format: () => null },
  "equipment_service.created": {
    label: "Skade meldt", color: RED, filter: "utstyr",
    format: (p) => (p.usable === false ? "Kan ikke brukes" : null),
  },
  "equipment_service.updated": {
    label: "Servicesak endret", color: AMBER, filter: "utstyr",
    format: (p) => SERVICE_STATUS_NB[str(p.status) ?? ""] ?? null,
  },
  "equipment_service_tickets.deleted": {
    label: "Servicesak slettet", color: RED, filter: "utstyr", format: () => null,
  },
  // Varsler i bjella fra equipment-reminders (VIB-2921, VIB-2933). Aktør er den som meldte
  // skaden; de daglige varslene er system.
  "equipment_notification.sent": {
    label: "Utstyrsvarsel sendt", color: BLUE, filter: "utstyr",
    format: (p) => [NOTIFICATION_KIND_NB[str(p.kind) ?? ""], str(p.recipient_name)].filter(Boolean).join(" til ") || null,
  },
  "equipment_notification.failed": {
    label: "Varsel ikke sendt", color: AMBER, filter: "utstyr",
    format: (p) => [NOTIFICATION_KIND_NB[str(p.kind) ?? ""], str(p.recipient_name)].filter(Boolean).join(" til ") || null,
  },
  "user.equipment_manager_changed": {
    label: "Utstyrsansvarlig endret", color: AMBER, filter: "utstyr",
    format: (p) => [str(p.user_name), p.new_value === true ? "på" : "av"].filter(Boolean).join(": ") || null,
  },
  "user.department_changed": {
    label: "Avdeling endret", color: BLUE, filter: "utstyr",
    format: (p) => [str(p.user_name), str(p.new_department) ?? "ingen"].filter(Boolean).join(": ") || null,
  },
  // Utstyrsbooking (VIB-2919). Skrevet av triggerne på equipment_booking_items og
  // equipment_bookings, både i event_log (utstyrskortet) og project_events (prosjektloggen).
  "equipment_booking.item_added": {
    label: "Utstyr booket", color: GREEN, filter: "utstyr",
    format: (p) => bookingLineDetail(p),
  },
  "equipment_booking.item_updated": {
    label: "Utstyrsbooking endret", color: BLUE, filter: "utstyr",
    format: (p) => {
      const detail = bookingLineDetail(p);
      const from = str(p.old_date_from);
      const to = str(p.old_date_to);
      const old = from && to ? `før ${formatIsoRangeNb(from, to)}` : null;
      return [detail, old].filter(Boolean).join(" · ") || null;
    },
  },
  "equipment_booking.item_removed": {
    label: "Utstyr fjernet fra booking", color: GRAY, filter: "utstyr",
    format: (p) => bookingLineDetail(p),
  },
  "equipment_booking.created": {
    label: "Utstyrsbooking opprettet", color: GREEN, filter: "utstyr",
    format: (p) => str(p.label),
  },
  "equipment_booking.updated": {
    label: "Utstyrsbooking endret", color: BLUE, filter: "utstyr",
    format: (p) => [str(p.label), str(p.responsible_name) ? `ansvarlig ${str(p.responsible_name)}` : null].filter(Boolean).join(" · ") || null,
  },
  "equipment_booking.cancelled": {
    label: "Utstyrsbooking avbestilt", color: RED, filter: "utstyr",
    format: (p) => {
      const from = str(p.date_from);
      const to = str(p.date_to);
      return [str(p.label), from && to ? formatIsoRangeNb(from, to) : null].filter(Boolean).join(" · ") || null;
    },
  },
  "equipment_booking_items.deleted": {
    label: "Bookinglinje slettet", color: GRAY, filter: "utstyr", format: () => null,
  },
  // Produktdata per modell (VIB-2918). Skrevet av DB-triggere på equipment_products og
  // equipment_media; edge functions setter created_by/enriched_by så aktøren blir caller.
  "equipment_product.created": {
    label: "Utstyrsmodell opprettet", color: GREEN, filter: "utstyr",
    format: (p) => [str(p.brand), str(p.model)].filter(Boolean).join(" ") || null,
  },
  "equipment_product.updated": {
    label: "Utstyrsmodell endret", color: BLUE, filter: "utstyr",
    format: (p) => {
      const changed = Array.isArray(p.changed) ? (p.changed as unknown[]).filter((c): c is string => typeof c === "string") : [];
      const fields = [...new Set(changed.map((c) => EQUIPMENT_PRODUCT_FIELD_NB[c] ?? c))];
      return [[str(p.brand), str(p.model)].filter(Boolean).join(" "), fields.join(", ")].filter(Boolean).join(": ") || null;
    },
  },
  "equipment_product.enriched": {
    label: "Produktdata lagret", color: GREEN, filter: "utstyr",
    format: (p) => [str(p.brand), str(p.model)].filter(Boolean).join(" ") || null,
  },
  "equipment_products.deleted": {
    label: "Utstyrsmodell slettet", color: RED, filter: "utstyr",
    format: (p) => {
      const old = (p.old ?? {}) as Record<string, unknown>;
      return [str(old.brand_name), str(old.model)].filter(Boolean).join(" ") || null;
    },
  },
  "equipment_media.added": {
    label: "Fil lagt til", color: BLUE, filter: "utstyr",
    format: (p) => [MEDIA_KIND_NB[str(p.kind) ?? ""], str(p.title)].filter(Boolean).join(": ") || null,
  },
  "equipment_media.removed": {
    label: "Fil fjernet", color: GRAY, filter: "utstyr",
    format: (p) => [MEDIA_KIND_NB[str(p.kind) ?? ""], str(p.title)].filter(Boolean).join(": ") || null,
  },
};

/** Hendelser fra frontend for utstyr (VIB-2917). Resten skrives av DB-triggere og importen. */
export const LOCATION_EVENTS = {
  imported: "location.imported",
} as const;

export const EQUIPMENT_EVENTS = {
  imported: "equipment_item.imported",
  imageAdded: "equipment_item.image_added",
  imageRemoved: "equipment_item.image_removed",
} as const;

export function eventLabel(eventType: string): string {
  return EVENT_TYPES[eventType]?.label ?? eventType;
}
export function eventColor(eventType: string): string {
  return EVENT_TYPES[eventType]?.color ?? GRAY;
}
export function eventDetail(eventType: string, payload: Record<string, unknown> | null | undefined): string | null {
  const def = EVENT_TYPES[eventType];
  if (!def?.format || !payload) return null;
  return def.format(payload);
}
/** event_types for en filter-kategori; null = alle (ingen IN-filter). */
export function eventTypesForFilter(filter: EventFilter): string[] | null {
  if (filter === "all") return null;
  return Object.entries(EVENT_TYPES).filter(([, d]) => d.filter === filter).map(([t]) => t);
}
